import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Chip } from '@rentspree/component-2023.components.atoms.chip';
import { IconButton } from '@rentspree/component-2023.components.atoms.icon-button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { Card } from '@rentspree/component-2023.components.organisms.card';

import { TopNavigation } from '../components/organisms/TopNavigation';

// Example ListingCard component using shared components (replace with actual shared components)
interface Listing {
  id: string;
  image: string;
  status: string;
  title: string;
  address: string;
  price: string;
  extra?: string;
}

// Use the mock image from the Card documentation example
const mockCardImage =
  'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80';

const listings: Listing[] = [
  {
    id: '1',
    image: mockCardImage,
    status: 'Active',
    title: 'Modern Downtown Apartment',
    address: '123 Main St, Downtown',
    price: '$2,400/month',
    extra: '3 Applications',
  },
  {
    id: '2',
    image: mockCardImage,
    status: 'Active',
    title: 'Suburban Family Home',
    address: '456 Oak Ave, Suburbs',
    price: '$3,200/month',
    extra: '0 Application',
  },
  {
    id: '3',
    image: mockCardImage,
    status: 'Draft',
    title: 'Luxury City Condo',
    address: '789 High St, Uptown',
    price: 'Price not set',
    extra: 'Not yet published',
  },
  {
    id: '4',
    image: mockCardImage,
    status: 'Draft',
    title: 'Charming Townhouse',
    address: '567 Elm St, Historic District',
    price: '$2,800/month',
    extra: 'Not yet published',
  },
  {
    id: '5',
    image: mockCardImage,
    status: 'Inactive',
    title: 'Compact Studio',
    address: '321 Pine St, Midtown',
    price: '$1,800/month',
  },
  {
    id: '6',
    image: mockCardImage,
    status: 'Occupied',
    title: 'Executive Penthouse',
    address: '890 Tower Blvd, Financial District',
    price: '$4,500/month',
    extra: 'Tenant: John Smith',
  },
];

const statusChipConfig: Record<
  string,
  { color: 'green' | 'yellow' | 'gray' | 'primary'; icon?: React.ReactElement }
> = {
  Active: {
    color: 'green',
    icon: <CheckCircleIcon fontSize='small' style={{ marginRight: 4 }} />,
  },
  Draft: {
    color: 'gray',
    icon: (
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#b5b5b5',
          marginRight: 4,
        }}
      />
    ),
  },
  Inactive: {
    color: 'gray',
    icon: (
      <RemoveCircleOutlineIcon fontSize='small' style={{ marginRight: 4 }} />
    ),
  },
  Occupied: { color: 'primary' },
};

const ListingCard: React.FC<Listing & { onClick?: () => void }> = ({
  image,
  status,
  title,
  address,
  price,
  extra,
  onClick,
}) => {
  const chipConfig = statusChipConfig[status] || { color: 'gray' };
  return (
    <Card
      style={{
        width: 290,
        margin: 12,
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <div style={{ position: 'relative' }}>
        <img
          src={image}
          alt={title}
          style={{
            width: '100%',
            height: 192,
            objectFit: 'cover',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'block',
          }}
        />
        <Chip
          color={chipConfig.color}
          label={
            <Typography variant='caption' style={{ fontWeight: 500 }}>
              {status}
            </Typography>
          }
          variant='filled'
          size='medium'
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            borderRadius: 9999,
          }}
          aria-label={status + ' status'}
        />
      </div>
      <div
        style={{
          padding: 16,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          position: 'relative',
          minHeight: 120,
        }}
      >
        <Typography variant='h6' style={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          {address}
        </Typography>
        <Typography
          variant='subtitle1'
          style={{
            color: price === 'Price not set' ? '#6b7280' : '#111827',
            fontWeight: 700,
          }}
        >
          {price}
        </Typography>
        {extra && (
          <Chip
            color={extra.startsWith('Tenant:') ? 'green' : 'primary'}
            label={
              <Typography variant='caption' style={{ fontWeight: 500 }}>
                {extra}
              </Typography>
            }
            size='small'
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              borderRadius: 9999,
            }}
            aria-label={extra}
          />
        )}
      </div>
    </Card>
  );
};

const ListingsPage: React.FC = () => {
  const navigate = useNavigate();

  const handleMenuItemClick = (_item: string) => {
    // Handle navigation between menu items
    // console.log(`Navigating to: ${item}`);
  };

  return (
    <div
      style={{
        background: '#f9fafb',
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <TopNavigation
        activeItem='listings'
        onMenuItemClick={handleMenuItemClick}
        userAvatarSrc='https://mui.com/static/images/avatar/1.jpg'
        userAvatarAlt='John Doe'
        className='mb-8'
      />
      <main style={{ width: 1280, margin: '0 auto', paddingTop: 32 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 32,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <Typography variant='h4' style={{ margin: 0, fontWeight: 700 }}>
              Your listings
            </Typography>
            <div
              style={{
                display: 'flex',
                gap: 8,
                background: '#fff',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                padding: 4,
              }}
            >
              <Button variant='contained' color='primary' size='medium'>
                All
              </Button>
              <Button variant='text' color='secondary' size='medium'>
                Active
              </Button>
              <Button variant='text' color='secondary' size='medium'>
                Inactive
              </Button>
              <Button variant='text' color='secondary' size='medium'>
                Drafts
              </Button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <IconButton
              aria-label='Search listings'
              style={{
                border: '1px solid #d1d5db',
                borderRadius: 8,
                height: 56,
                width: 56,
                backgroundColor: '#fff',
              }}
            >
              <SearchIcon style={{ fontSize: 20, color: '#6b7280' }} />
            </IconButton>
            <Button
              variant='outlined'
              color='primary'
              size='large'
              onClick={() => navigate('/add-tenant-screening')}
            >
              Screen Tenant
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='large'
              startIcon={<AddIcon fontSize='small' />}
              onClick={() => navigate('/add-listing')}
            >
              Add Listing
            </Button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          {listings.map(listing => (
            <ListingCard
              key={listing.id}
              {...listing}
              onClick={() => navigate(`/listing/${listing.id}`)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default ListingsPage;
